import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, UserPlus, UserX, AlertTriangle, Edit, Trash2, Crown, User } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminManageAdmins = () => {
  const { user } = useAuth();
  const isPlatformOwner = user?.platform_role === 'platform_owner';
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showOwnerConfirm, setShowOwnerConfirm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    platform_role: 'platform_staff'
  });

  useEffect(() => {
    if (!isPlatformOwner) {
      toast.error('Access denied - Platform Owner only');
      return;
    }
    fetchAdmins();
  }, [isPlatformOwner]);

  useEffect(() => {
    // Reset form when opening/closing to prevent pre-filled data
    if (showCreateForm) {
      setFormData({
        email: '',
        full_name: '',
        password: '',
        platform_role: 'platform_staff'
      });
    }
  }, [showCreateForm]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users?role=admin`);
      setAdmins(response.data.users || []);
    } catch (error) {
      toast.error('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const createAdmin = async (e) => {
    e.preventDefault();
    
    // Check if creating platform owner - require confirmation
    if (formData.platform_role === 'platform_owner' && !showOwnerConfirm) {
      setShowOwnerConfirm(true);
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      await axios.post(`${API}/admin/create-admin`, formData);
      toast.success('Admin account created successfully');
      setFormData({ email: '', full_name: '', password: '', platform_role: 'platform_staff' });
      setShowCreateForm(false);
      setShowOwnerConfirm(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const toggleAdminStatus = async (adminId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    const reason = prompt(`Reason for ${newStatus === 'suspended' ? 'suspending' : 'activating'} this admin:`);
    
    if (!reason) return;

    try {
      await axios.patch(`${API}/admin/users/${adminId}/status`, { status: newStatus, reason });
      toast.success(`Admin ${newStatus}`);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleChangePlatformRole = async (admin, newRole) => {
    // Protect primary owner
    if (admin.email === 'vincotek25@gmail.com' && newRole !== 'platform_owner') {
      toast.error('Cannot demote primary owner');
      return;
    }
    
    const reason = prompt(`Reason for changing ${admin.full_name}'s platform role to ${newRole}:`);
    if (!reason) return;
    
    const confirm = window.confirm(
      `Change ${admin.full_name}'s platform role from ${admin.platform_role || 'none'} to ${newRole}?`
    );
    if (!confirm) return;
    
    try {
      await axios.patch(`${API}/admin/manage-admins/${admin.id}/platform-role?reason=${encodeURIComponent(reason)}`, {
        platform_role: newRole
      });
      toast.success('Platform role updated');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update platform role');
    }
  };

  const handleDeleteAdmin = async (admin) => {
    // Protect primary owner
    if (admin.email === 'vincotek25@gmail.com') {
      toast.error('Cannot delete primary owner account');
      return;
    }
    
    const reason = prompt(`Reason for deleting ${admin.full_name}:`);
    if (!reason) return;
    
    const confirm = window.confirm(
      `Are you sure you want to delete ${admin.full_name} (${admin.email})? This action cannot be undone.`
    );
    if (!confirm) return;
    
    try {
      await axios.delete(`${API}/admin/users/${admin.id}?reason=${encodeURIComponent(reason)}`);
      toast.success('Admin deleted successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  if (!isPlatformOwner) {
    return (
      <AdminLayout>
        <div className="text-center py-20">
          <Shield className="w-16 h-16 mx-auto mb-4 text-red-600" />
          <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground">This page is only accessible to Platform Owners</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 flex items-center gap-2">
              <Shield className="w-8 h-8 text-purple-600" />
              Manage Administrators
            </h1>
            <p className="text-muted-foreground">Create and manage admin accounts (Super Admin only)</p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <UserPlus className="w-4 h-4" />
            Create Admin
          </button>
        </div>

        {/* Warning Banner */}
        <Card className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-yellow-800 dark:text-yellow-200">Platform Owner Only</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                  This page allows creation and management of Platform Console admin accounts. Use with caution.
                  Passwords are hashed and never displayed after creation. Admins must change password on first login.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Form */}
        {showCreateForm && (
          <Card>
            <CardHeader>
              <CardTitle>Create New Administrator</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={createAdmin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Email Address</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="admin@gofella.com"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Full Name</label>
                  <Input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    placeholder="John Smith"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Initial Password</label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    placeholder="Min 8 chars with complexity"
                    autoComplete="new-password"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Password will be hashed and never displayed again. Admin must change on first login.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-1">Platform Role</label>
                  <select
                    value={formData.platform_role}
                    onChange={(e) => setFormData({ ...formData, platform_role: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    <option value="platform_staff">Platform Staff</option>
                    <option value="platform_owner">Platform Owner</option>
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Platform Owner has full control. Platform Staff can manage but cannot create owners.
                  </p>
                </div>
                <div className="flex gap-3">
                  <button type="submit" className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 font-semibold">
                    Create Admin Account
                  </button>
                  <button type="button" onClick={() => {setShowCreateForm(false); setShowOwnerConfirm(false);}} className="flex-1 border px-4 py-3 rounded-lg hover:bg-muted">
                    Cancel
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Admins List */}
        <Card>
          <CardHeader>
            <CardTitle>Current Administrators ({admins.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : admins.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No admins found</div>
              ) : (
                admins.map((admin) => (
                  <div key={admin.id} className="p-4 border border-border rounded-lg hover:bg-muted/50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="font-semibold text-foreground text-lg">{admin.full_name}</div>
                        <div className="text-sm text-muted-foreground">{admin.email}</div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Badge className={
                            admin.platform_role === 'platform_owner' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : admin.platform_role === 'platform_staff'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800'
                          }>
                            {admin.platform_role === 'platform_owner' ? 'Platform Owner' : 
                             admin.platform_role === 'platform_staff' ? 'Platform Staff' : 
                             admin.admin_sub_role || 'Admin (legacy)'}
                          </Badge>
                          <Badge className={admin.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}>
                            {admin.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-2 space-y-1">
                          <div>Created: {new Date(admin.created_at).toLocaleDateString()}</div>
                          {admin.last_login_at && (
                            <div>Last Login: {new Date(admin.last_login_at).toLocaleString()}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {/* Platform Role Actions */}
                        {admin.email !== 'vincotek25@gmail.com' && admin.id !== user.id && (
                          <>
                            {admin.platform_role !== 'platform_owner' && (
                              <button
                                onClick={() => handleChangePlatformRole(admin, 'platform_owner')}
                                className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-600"
                                title="Promote to Platform Owner"
                              >
                                <Crown className="w-4 h-4" />
                              </button>
                            )}
                            {admin.platform_role === 'platform_owner' && (
                              <button
                                onClick={() => handleChangePlatformRole(admin, 'platform_staff')}
                                className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-blue-600"
                                title="Demote to Platform Staff"
                              >
                                <User className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteAdmin(admin)}
                              className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-600"
                              title="Delete admin"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        
                        {/* Status Toggle */}
                        {admin.platform_role !== 'platform_owner' && admin.id !== user.id && (
                          <button
                            onClick={() => toggleAdminStatus(admin.id, admin.status)}
                            className={`p-2 rounded-lg ${admin.status === 'active' ? 'hover:bg-red-50 text-red-600' : 'hover:bg-green-50 text-green-600'}`}
                            title={admin.status === 'active' ? 'Suspend' : 'Activate'}
                          >
                            {admin.status === 'active' ? <UserX className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
                          </button>
                        )}
                        
                        {/* Primary Owner Badge */}
                        {admin.email === 'vincotek25@gmail.com' && (
                          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Primary Owner (Protected)
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Platform Owner Confirmation Dialog */}
        {showOwnerConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4 border border-border">
              <h3 className="text-xl font-bold text-foreground mb-4">Confirm Platform Owner Creation</h3>
              <p className="text-sm text-muted-foreground mb-6">
                You are about to create a new <strong>Platform Owner</strong> account. This role has full administrative access to the Platform Console, including the ability to:
              </p>
              <ul className="text-sm text-muted-foreground space-y-2 mb-6 list-disc pl-5">
                <li>Create and manage other admins</li>
                <li>Modify system feature flags</li>
                <li>Access all user data and admin tools</li>
                <li>Grant or revoke platform roles</li>
              </ul>
              <p className="text-sm font-semibold text-foreground mb-6">
                Are you sure you want to proceed?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowOwnerConfirm(false)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-muted text-foreground"
                >
                  Cancel
                </button>
                <button
                  onClick={createAdmin}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminManageAdmins;
